<template>
    <div v-if="!loading" class="tab-pane active mt-3">
        <p class="mb-0">{{ $t('To scroll right hold down SHIFT and scroll or drag the area below') }}</p>
        <div class="scrolling-wrapper row flex-row flex-nowrap pt-5 pb-4"
             v-drag-and-drop:options="options"
             v-dragscroll:nochilddrag>
            <div v-for="vehicle in vehicles" :key="vehicle.id" class="col-lg-2 col-md-3 col-sm-12">
                <app-card :padding="'0.35rem'">
                    <div>
                        <div class="d-flex justify-content-between">
                            <p data-toggle="collapse" :href="`#vehicle_info_${vehicle.id}`" class="pointer mb-0">
                                {{ vehicle.make.name }} {{ vehicle.model.name }} <app-vehicle-no :slug="vehicle.slug"/>
                            </p>
                            <a class="btn btn-xs btn-info m-0 text-white font-14px" @click="$modal.show('create_booking_comments', { vehicle, booking: false })">
                                <fa-icon :icon="['fas', 'comments']" class=""/>
                            </a>
                        </div>
                        <div class="d-flex justify-content-between">
                            <small class="pt-1">{{ vehicle.reg_no }}, {{ $t(vehicle.transmission.name) }}</small>
                            <router-link v-if="!vehicle.is_empty" :to="{name:'booking.create', query: {vehicle: vehicle.id}}" class="btn btn-xs btn-success float-right mr-0 text-white">
                                {{ $t('Book') }}
                            </router-link>
                        </div>
                        <div :id="`vehicle_info_${vehicle.id}`" class="panel-collapse collapse">
                            <p class="dark-grey-text p-300-sm">
                                {{ vehicle.body_type[locale] }}, {{ $t(vehicle.fuel.name) }}
                            </p>
                            <p class="dark-grey-text p-300-sm">
                                {{ $t('Year') }}: {{ vehicle.year }}<br>
                                {{ $t('Engine') }}: {{ vehicle.engine }}<br>
                                {{ $t('Color') }}: {{ vehicle.color[locale] }}
                            </p>
                            <p class="dark-grey-text p-300-sm">
                                {{ $t('Equipment') }}: {{ vehicle.equipment[locale] }}
                                <br>
                                {{ $t('Damages') }}: {{ vehicle.damages[locale] }}
                                <br>
                                {{ $t('Comments') }} {{ $t('(in house)') }}: <br>
                                {{ vehicle.comments }}
                            </p>
                        </div>
                    </div>
                    <vue-draggable-group v-model="vehicle.items"
                                         :groups="vehicles"
                                         :data-id="vehicle.id"
                                         @change="onGroupsChange">
                        <div class="drag-inner-list" :data-id="vehicle.id">
                            <div v-for="booking in vehicle.items"
                                 :key="booking.id"
                                 :data-id="booking.id"
                                 class="pb-3 drag-item">
                                <app-card class="grab" :padding="'0.35rem'">
                                    <template v-if="!booking.is_comment">
                                        <h6>{{ booking.start_time | formatDate }} - {{ booking.end_time | formatDate }}</h6>
                                        <div class="d-flex justify-content-between">
                                            <small>
                                                kl {{ booking.start_time | formatTime }}/{{ booking.end_time | formatTime }}
                                            </small>
                                            <app-button native-type="button" type="success" @click="earlyReturn(booking.id)" xsmall>
                                                {{ $t('Early return') }}
                                            </app-button>
                                        </div>
                                        <p class="dark-grey-text p-300-sm font-small">
                                            <small>
                                                {{ booking.pickup_location.name[locale] }}/{{ booking.return_location.name[locale] }}
                                                <br>
                                                {{ booking.fname }} {{ booking.lname }}, {{ booking.phone }},
                                                {{ $t(langs.find(l => l.key === booking.lang).name) }}
                                            </small>
                                        </p>
                                        <p class="mb-1 font-small">
                                            <a data-toggle="collapse" :href="`#info_${booking.id}`">{{ $t('More info') }}...</a>
                                        </p>
                                        <div :id="`info_${booking.id}`" class="panel-collapse collapse font-small">
                                            <p class="dark-grey-text p-300-sm">
                                                <small>
                                                    {{ $t('Price') }}: {{ (booking.prices.total / 100).toFixed(2) }} €<br>
                                                    {{ $t('Bond') }}: {{ (booking.prices.bond / 100).toFixed(2) }} €<br>
                                                    {{ $t('Liability') }}: {{ vehicle.liability_sum / 100 }} €
                                                </small>
                                            </p>
                                            <p class="dark-grey-text p-300-sm mb-0" v-if="booking.additional_eq.length">
                                                <template v-for="(eq, i) in additionNames(booking.additional_eq)">
                                                    {{ eq[locale] }} <br :key="i"/>
                                                </template>
                                            </p>
                                        </div>
                                        <router-link :to="{name: 'booking.edit', params: {booking_id: booking.id}}"
                                                     class="btn btn-primary btn-xsm text-white">
                                            {{ $t('Edit') }}
                                        </router-link>
                                        <router-link v-if="$store.getters.canViewContracts && booking.contract"
                                                     :to="{name: 'contracts.show', params: {contract_id: booking.contract.id}}"
                                                     class="btn btn-info btn-xsm text-white">
                                            {{ $t('Contract') }}
                                        </router-link>
                                        <app-button native-type="button"
                                                    type="danger"
                                                    @click="destroy(vehicle.id, booking.id)"
                                                    v-if="booking.confirmed_at == null || !booking.contract"
                                                    xsmall>
                                            <fa-icon :icon="['fa', 'trash-alt']"/>
                                        </app-button>
                                        <div class="float-right font-small">
                                            <template v-if="booking.confirmed_at">
                                                {{ booking.confirmed_at | formatDateTime }} <br>
                                                <a v-if="!booking.contract" class="btn-link text-danger" @click="confirmBooking(vehicle.id, booking.id, false)">
                                                    {{ $t('Unconfirm') }}
                                                </a>
                                            </template>
                                            <template v-else>
                                                <app-button native-type="button" type="success" @click="confirmBooking(vehicle.id, booking.id, true)" xsmall>
                                                    {{ $t('Confirm') }}
                                                </app-button>
                                            </template>
                                        </div>
                                        <div class="clearfix"></div>
                                    </template>
                                    <template v-else>
                                        <div class="d-flex justify-content-between">
                                            {{ $t('Comments') }}
                                            <h6>{{ booking.start_time | formatDate }}</h6>
                                        </div>
                                        <p class="dark-grey-text p-300-sm font-small mb-2">
                                            {{ booking.comments }}
                                        </p>
                                        <div class="d-flex justify-content-between">
                                            <p class="dark-grey-text p-300-sm font-small mb-0">
                                                <small>
                                                    {{ booking.fname }}
                                                </small>
                                            </p>
                                            <div>
                                                <app-button native-type="button" class="btn btn-primary btn-xsm text-white" @click="$modal.show('create_booking_comments', { vehicle: false, booking })">
                                                    {{ $t('Edit') }}
                                                </app-button>
                                                <app-button native-type="button"
                                                            type="danger"
                                                            @click="destroy(vehicle.id, booking.id)"
                                                            xsmall>
                                                    <fa-icon :icon="['fa', 'trash-alt']"/>
                                                </app-button>
                                            </div>
                                        </div>
                                    </template>
                                </app-card>
                            </div>
                        </div>
                    </vue-draggable-group>
                </app-card>
            </div>
        </div>
        <div class="clearfix"></div>
        <add-comments-modal @setBookingComments="setBookingComments" />
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import Vue from 'vue';
import DashboardService from '@services/dashboard.service';
import BookingService from '@services/booking.service';
import {dragscroll} from 'vue-dragscroll';
import AddCommentsModal from './modals/AddComments';

export default {
    directives: {
        dragscroll
    },
    props: {
        additions: Array
    },
    data() {
        return {
            vehicles: null,
            slug: this.$route.params.group_slug,
            loading: false,
            options: {
                dropzoneSelector: '.drag-inner-list',
                draggableSelector: '.drag-item'
            }
        };
    },
    computed: {
        ...mapGetters(['locale', 'langs'])
    },
    methods: {
        setBookingComments(booking) {
            const vehicle = this.vehicles.find(veh => veh.id === booking.vehicle_id);
            const index = vehicle.items.findIndex(b => b.id === booking.id);

            if (index > -1) {
                Vue.set(vehicle.items, index, booking);
            } else {
                vehicle.items.push(booking);
            }
        },
        onGroupsChange(data) {
            this.loading = true;
            let form = {
                vehicles: data
            };
            DashboardService.reorderBookings(form).then(res => {
                this.loading = false;
                this.$toastr.s(this.$t('Updated'));
            });
        },
        additionNames(additionIds) {
            let addEqs = [];
            additionIds.map(id => {
                addEqs.push(this.additions.some(add => add.id === id) ? this.additions.find(add => add.id === id).name : {});
            });
            return addEqs;
        },
        getGroupVehicles() {
            this.loading = true;
            DashboardService.groupVehicles(this.slug).then(res => {
                this.vehicles = res;
                this.vehicles.map(vehicle => {
                    vehicle.items = vehicle.bookings;
                });
                this.loading = false;
            });
        },
        earlyReturn(bookingId) {
            if (!window.confirm(`${this.$t('Vehicle returned early')}?`)) {
                return false;
            }
            this.$router.push({name: 'booking.edit', params: {booking_id: bookingId}, query: { early: 'true' }});
        },
        destroy(vehicleId, bookingId) {
            if (!window.confirm(`${this.$t('Delete')}?`)) {
                return false;
            }
            BookingService.destroy(bookingId).then(res => {
                const vehicle = this.vehicles.find(veh => veh.id === vehicleId);
                const index = vehicle.items.findIndex(b => b.id === bookingId);
                vehicle.items.splice(index, 1);
                this.$toastr.s(this.$t('Deleted'));
            });
        },
        confirmBooking(vehicleId, bookingId, confirm) {
            let msg = this.$t('Unconfirm');
            if (confirm) {
                msg = this.$t('Confirm');
            }
            if (!window.confirm(`${msg}?`)) {
                return false;
            }
            BookingService.confirmBooking(bookingId).then(res => {
                const vehicle = this.vehicles.find(veh => veh.id === vehicleId);
                const index = vehicle.items.findIndex(b => b.id === bookingId);
                Vue.set(vehicle.items, index, res);
                this.$toastr.s(this.$t('Updated'));
            });
        },
    },
    components: {
        AddCommentsModal
    },
    created() {
        this.getGroupVehicles();
    },
    beforeRouteUpdate(to, from, next) {
        this.slug = to.params.group_slug;
        this.getGroupVehicles();
        next();
    },
};
</script>
<style lang="scss" scoped>
.scrolling-wrapper {
    overflow-x: auto;
}

.drag-inner-list {
    background: #f8f8f8;
    border: 1px black dashed;
    padding: 15px;
}
</style>
